import { useCallback, useReducer, useRef } from 'react';
import { fetchAssemblyHasChildren, fetchBoms, fetchChildren, fetchChildRows, fetchEcoHasItems, isLeafView } from '../api/orcanosClient';
import { bomStructure, getSettings } from '../settings/settingsStore';
import { getQuantity, getUnitCost } from '../utils/cost';
import { useToast } from '../ui/Toast';

// ─────────────────────────────────────────────────────────────────────────
// Tree state model
//
// We keep the whole tree as a flat list of "nodes". Each node represents
// one row in the rendered grid. Render order = list order. Depth and
// parent are tracked per-node.
//
//   node = {
//     nodeKey:    unique-per-app-session string. INCLUDES THE PARENT KEY
//                 so the same Orcanos id appearing under different parents
//                 produces distinct keys (a part can legitimately be reused
//                 in multiple BOMs).
//     row:        normalized row from the API
//     parentKey:  nodeKey of the parent, or null for root rows
//     depth:      0 for top-level
//     isRoot:     true for top-level rows
//     expanded:   true when children are currently shown
//     loading:    true while a child fetch is in flight
//     loaded:     true after a successful child fetch
//     childCount: number of immediate children loaded (after first expand)
//     errored:    true if last fetch failed
//   }
//
// Children are cached by `nodeKey` (NOT by Orcanos id), so a part appearing
// in two different BOMs is fetched twice. That's correct: the API call is
// the same and would return the same children, but caching by nodeKey keeps
// the data structures simple and avoids edge cases where a "shared" cache
// entry gets attached to two parents simultaneously.
// ─────────────────────────────────────────────────────────────────────────

const EXPAND_ALL_DEPTH_CAP = 20;
const EXPAND_ALL_CONCURRENCY = 6;
const PROBE_CONCURRENCY = 6;

// The on-demand cost walk (resolveSubtreeCost) recurses instead of pumping a
// level-order frontier, so it can't bound itself with mapWithLimit — a plain
// nested limit would still fan out 6^depth. The limiter below caps concurrent
// *requests* globally for the whole walk, which is the resource that matters.
const COST_WALK_CONCURRENCY = 6;
const COST_WALK_PAGE_SIZE = 500;

function makeFetchLimiter(max) {
  let active = 0;
  const queue = [];
  return async function limit(fn) {
    if (active >= max) await new Promise((resolve) => queue.push(resolve));
    active++;
    try {
      return await fn();
    } finally {
      active--;
      queue.shift()?.();
    }
  };
}

// Run async fn over `items` with at most `limit` in flight at a time.
// Returns results in the same order as `items`.
async function mapWithLimit(items, limit, fn) {
  const out = new Array(items.length);
  let nextIdx = 0;
  async function worker() {
    while (true) {
      const i = nextIdx++;
      if (i >= items.length) return;
      out[i] = await fn(items[i], i);
    }
  }
  const workers = Array.from({ length: Math.min(limit, items.length) }, worker);
  await Promise.all(workers);
  return out;
}

let _seq = 0;
function nextNodeKey() {
  _seq += 1;
  return `n${_seq}`;
}

function makeNode(row, { parentKey = null, depth = 0, isRoot = false } = {}) {
  return {
    nodeKey: nextNodeKey(),
    row,
    parentKey,
    depth,
    isRoot,
    expanded: false,
    loading: false,
    loaded: false,
    childCount: 0,
    errored: false,
  };
}

// `topPageSize` is filled in by `useReducer`'s lazy initializer below from
// `getSettings().pageSize` so changing `<pageSize>` in settings.xml takes
// effect on next reload — no code change needed.
const initialState = {
  nodes: [],
  topLoading: false,
  topError: null,
  topPage: 1,
  topPageSize: 50,
  // `Total_records` IS truthful when fetchBoms is called with the right
  // IsNewPaging/IsReturnPageCount combo (see orcanosClient.js). We also keep
  // a `topHasMore` heuristic so the Next button still works if a future
  // filter ever stops returning a real total.
  topTotal: 0,
  topHasMore: false,
};

function findIndexByKey(nodes, key) {
  return nodes.findIndex((n) => n.nodeKey === key);
}

function reducer(state, action) {
  switch (action.type) {
    case 'TOP_LOAD_START':
      return { ...state, topLoading: true, topError: null };

    case 'TOP_LOAD_OK': {
      const { rows } = action;
      return {
        ...state,
        topLoading: false,
        topError: null,
        topPage: action.page,
        topPageSize: action.pageSize,
        topTotal: action.total,
        topHasMore: rows.length >= action.pageSize,
        nodes: rows.map((row) => makeNode(row, { depth: 0, isRoot: true })),
      };
    }

    case 'ROOTS_PROBED': {
      // action.hasKids: originalId -> true / false / null (check failed: unknown, so the row
      // gets its chevron back). Every answered row stops `checking`.
      const { hasKids } = action;
      const nodes = state.nodes.map((n) => {
        if (!n.isRoot || !(n.row.originalId in hasKids)) return n;
        const v = hasKids[n.row.originalId];
        return { ...n, row: { ...n.row, checking: false, ...(v === null ? {} : { hasChildren: v }) } };
      });
      return { ...state, nodes };
    }

    case 'TOP_LOAD_FAIL':
      return {
        ...state,
        topLoading: false,
        topError: action.error,
        nodes: [],
      };

    case 'CHILD_LOAD_START': {
      const nodes = state.nodes.map((n) =>
        n.nodeKey === action.parentKey
          ? { ...n, loading: true, errored: false }
          : n
      );
      return { ...state, nodes };
    }

    case 'CHILD_LOAD_OK': {
      // Insert children right after the parent. If the parent is already
      // expanded (e.g. expand-all racing with a manual click), we no-op
      // rather than duplicate the children.
      //
      // The caller may supply pre-generated `childKeys` (one per row) so
      // expand-all can refer to children by key without re-reading state.
      // If omitted, we generate fresh keys here.
      const { parentKey, rows, childKeys } = action;
      const parentIdx = findIndexByKey(state.nodes, parentKey);
      if (parentIdx < 0) return state;
      const parent = state.nodes[parentIdx];
      if (parent.expanded && parent.loaded) return state;

      const newChildren = rows.map((row, i) => {
        const node = makeNode(row, {
          parentKey,
          depth: parent.depth + 1,
          isRoot: false,
        });
        if (childKeys && childKeys[i]) {
          node.nodeKey = childKeys[i];
        }
        return node;
      });

      const updatedParent = {
        ...parent,
        loading: false,
        loaded: true,
        expanded: true,
        childCount: rows.length,
        errored: false,
      };

      return {
        ...state,
        nodes: [
          ...state.nodes.slice(0, parentIdx),
          updatedParent,
          ...newChildren,
          ...state.nodes.slice(parentIdx + 1),
        ],
      };
    }

    case 'CHILD_LOAD_FAIL': {
      const nodes = state.nodes.map((n) =>
        n.nodeKey === action.parentKey
          ? { ...n, loading: false, errored: true }
          : n
      );
      return { ...state, nodes };
    }

    case 'EXPAND_FROM_CACHE': {
      // Re-show previously-loaded subtree.
      const { parentKey, cachedSubtree } = action;
      const parentIdx = findIndexByKey(state.nodes, parentKey);
      if (parentIdx < 0) return state;
      const parent = state.nodes[parentIdx];
      if (parent.expanded) return state; // already expanded; no-op

      const updatedParent = { ...parent, expanded: true };
      return {
        ...state,
        nodes: [
          ...state.nodes.slice(0, parentIdx),
          updatedParent,
          ...cachedSubtree,
          ...state.nodes.slice(parentIdx + 1),
        ],
      };
    }

    case 'COLLAPSE': {
      // Remove every descendant of `parentKey` from the flat list. Nodes
      // are stored contiguously: descendants follow the parent until we
      // hit a node with depth ≤ parent.depth.
      const { parentKey } = action;
      const parentIdx = findIndexByKey(state.nodes, parentKey);
      if (parentIdx < 0) return state;
      const parent = state.nodes[parentIdx];

      let endIdx = parentIdx + 1;
      while (
        endIdx < state.nodes.length &&
        state.nodes[endIdx].depth > parent.depth
      ) {
        endIdx++;
      }

      const updatedParent = { ...parent, expanded: false };
      return {
        ...state,
        nodes: [
          ...state.nodes.slice(0, parentIdx),
          updatedParent,
          ...state.nodes.slice(endIdx),
        ],
      };
    }

    default:
      return state;
  }
}

// ─────────────────────────────────────────────────────────────────────────
// Hook
//
// `topView` picks the top-level filter: 'boms' (the BOM filter) or 'parts'
// (the Part Catalog filter). The server maps it to this account's filter id.
//
// `topSearchQuery` is forwarded to the server, which builds the `Filter_By`
// clause. Changing it re-runs `loadTop(1)` automatically.
//
// `topWhereUsedId` narrows the top level to the BOMs that contain that part
// (the Where-Used flow). The server builds the clause from the id — the page
// never sends SQL. Changing it also re-runs `loadTop(1)` automatically.
// ─────────────────────────────────────────────────────────────────────────
export function useBomChildren({
  onAuthExpired,
  topView = 'boms',
  topSearchQuery = '',
  topWhereUsedId = null,
}) {
  const [state, dispatch] = useReducer(reducer, initialState, (init) => ({
    ...init,
    topPageSize: getSettings().pageSize ?? init.topPageSize,
  }));
  const { showToast } = useToast();

  // Callbacks from the parent are read through refs, so no loader below changes identity
  // when the parent re-renders. BomTree re-fetches page 1 whenever `loadTop` changes — a
  // callback in its dependencies turned every parent render into a full reload.
  const authExpiredRef = useRef(onAuthExpired);
  authExpiredRef.current = onAuthExpired;
  const toastRef = useRef(showToast);
  toastRef.current = showToast;

  // Increments on every top-level load. A reply that arrives after a newer load started
  // (a fast page click, a search typed mid-flight) is dropped instead of overwriting it.
  const topSeq = useRef(0);

  // What "expand" means depends on the view — a BOM expands into part instances, an ECO into
  // its parts and DMS items, a DMS item into nothing (fetchChildRows). Read through a ref so the
  // loaders below keep a stable identity; a tree is keyed by view, so it never changes in place.
  const viewRef = useRef(topView);
  viewRef.current = topView;

  // We need to read the latest nodes list inside async callbacks (especially
  // expandAll's pump) without retriggering the callback identity. A ref that
  // mirrors `state.nodes` does that.
  const nodesRef = useRef(state.nodes);
  nodesRef.current = state.nodes;

  // childCache: nodeKey -> { rows, subtree }
  //   rows    — the API rows from the last successful fetch (used for fresh expand)
  //   subtree — a snapshot of the descendants at collapse time (used for re-expand
  //             without re-fetching grand-children)
  const childCache = useRef(new Map());

  // probeRowsCache: parentOriginalId -> rows[]
  //
  // When the user expands a row, we probe each of its children for THEIR
  // children so we know whether to render the chevron. The probe is a
  // full fetchChildren call — its rows are stashed here keyed by the
  // probed row's `originalId`, so when the user later clicks `>` on one
  // of those children we have the data ready (no extra fetch).
  //
  // Top-level fetches (loadTop) do NOT probe. Probing only kicks in when
  // the user has expressed intent by clicking expand.
  const probeRowsCache = useRef(new Map());

  // subtreeCostCache: originalId -> Σ (qty × unit cost) over ALL descendants
  // of that part. Filled only by resolveSubtreeCost. Keyed by part rather than
  // by node, because the sub-tree under a given part is the same wherever that
  // part is used — so resolving it once answers every occurrence.
  const subtreeCostCache = useRef(new Map());

  // Caps concurrent requests across one cost walk. One limiter per tree.
  const limitFetch = useRef(makeFetchLimiter(COST_WALK_CONCURRENCY)).current;

  // ─── Reload top-level BOMs ─────────────────────────────────────────────
  const loadTop = useCallback(
    async (page = 1) => {
      // Guard against a stray React event being passed when an onClick is
      // wired as `onClick={loadTop}` instead of `onClick={() => loadTop(1)}`.
      // Passing an event into the request body triggers a circular-JSON
      // crash deep inside fetch.
      const pageNum =
        Number.isFinite(page) && page >= 1 ? Math.floor(page) : 1;
      const seq = ++topSeq.current;
      childCache.current.clear();
      probeRowsCache.current.clear();
      subtreeCostCache.current.clear();
      dispatch({ type: 'TOP_LOAD_START' });
      try {
        const pageSize = getSettings().pageSize ?? 50;
        const { rows, total } = await fetchBoms({
          page: pageNum,
          pageSize,
          view: topView,
          searchQuery: topSearchQuery,
          whereUsedId: topWhereUsedId,
        });
        if (seq !== topSeq.current) return;
        // A list whose rows never expand says so up front, or every row shows a chevron
        // that opens onto nothing (roots are not probed).
        // ECOs keep their chevron while /eco-has-items runs (~25 s a page on seeallai,
        // 2026-11-02) — hiding every arrow until then read as "expand is broken". The empty
        // ones lose it when the answer arrives.
        const topRows = isLeafView(topView)
          ? rows.map((r) => ({ ...r, hasChildren: false }))
          : rows;
        dispatch({ type: 'TOP_LOAD_OK', rows: topRows, total, page: pageNum, pageSize });
        // ECOs: ask which roots list any item, so an empty ECO shows no chevron. A light check
        // (/eco-has-items: one query drops the ECOs that list nothing, then one row per list), 50 ECOs per call, and each
        // answer is applied as it arrives — a whole page of full probes took long enough that
        // every arrow was still there when the user clicked. A failed check keeps the arrow.
        if (topView === 'ecos') {
          const ecos = rows.filter((r) => r.type === 'CR' && r.itemId);
          const chunks = [];
          for (let i = 0; i < ecos.length; i += 50) chunks.push(ecos.slice(i, i + 50));
          await mapWithLimit(chunks, 2, async (chunk) => {
            try {
              const has = await fetchEcoHasItems(chunk.map((r) => r.itemId));
              if (seq !== topSeq.current) return;
              const hasKids = {};
              chunk.forEach((r) => {
                const k = String(r.itemId);
                hasKids[r.originalId] = k in has ? !!has[k] : null;
              });
              dispatch({ type: 'ROOTS_PROBED', hasKids });
            } catch {
              // Unknown — give the chevrons back rather than hide an ECO's items.
              if (seq !== topSeq.current) return;
              dispatch({ type: 'ROOTS_PROBED', hasKids: Object.fromEntries(chunk.map((r) => [r.originalId, null])) });
            }
          });
        }
        // Assembly structure (user, 2026-11-02): the BOMs / Assembly lists' roots were never
        // probed, so every row showed the "unknown" icon until opened. One batched check per 50
        // rows (/assembly-has-children) sets the assembly icon, or drops the arrow of a childless
        // row. The arrow stays meanwhile — and stays for a row the check could not answer.
        if (topView === 'assemblies' || (topView === 'boms' && bomStructure() === 'assembly')) {
          const roots = rows.filter((r) => r.originalId && r.type !== 'DMS');
          const chunks = [];
          for (let i = 0; i < roots.length; i += 50) chunks.push(roots.slice(i, i + 50));
          await mapWithLimit(chunks, 2, async (chunk) => {
            try {
              const has = await fetchAssemblyHasChildren(chunk.map((r) => r.originalId));
              if (seq !== topSeq.current) return;
              const hasKids = {};
              chunk.forEach((r) => {
                const k = String(r.originalId);
                if (k in has) hasKids[r.originalId] = !!has[k];
              });
              dispatch({ type: 'ROOTS_PROBED', hasKids });
            } catch {
              /* unknown — the arrows stay */
            }
          });
        }
      } catch (err) {
        if (seq !== topSeq.current) return;
        if (err.httpCode === 401) {
          authExpiredRef.current?.(err.message);
          return;
        }
        dispatch({ type: 'TOP_LOAD_FAIL', error: err.message });
        toastRef.current(err.message || 'Failed to load BOMs.', 'error');
      }
    },
    [topView, topSearchQuery, topWhereUsedId]
  );

  // ─── Internal: load children of a node and dispatch CHILD_LOAD_OK ──────
  // Returns the rows that were attached, or null on failure / 401.
  // Uses the cached `rows` if available (any prior expand of this same node).
  const loadChildren = useCallback(
    async (nodeKey, row) => {
      const cached = childCache.current.get(nodeKey);
      if (cached?.rows) {
        dispatch({ type: 'CHILD_LOAD_OK', parentKey: nodeKey, rows: cached.rows });
        return cached.rows;
      }

      dispatch({ type: 'CHILD_LOAD_START', parentKey: nodeKey });
      try {
        // Step 1: fetch this row's children (the rows we'll display).
        // If a sibling-probe already fetched them, hit the cache.
        let rows;
        if (row.originalId && probeRowsCache.current.has(row.originalId)) {
          rows = probeRowsCache.current.get(row.originalId);
        } else {
          const result = await fetchChildRows(viewRef.current, row);
          rows = result.rows;
          if (row.originalId) {
            probeRowsCache.current.set(row.originalId, rows);
          }
          // An empty ECO otherwise just loses its chevron — say why, so it never reads as "nothing happened".
          if (viewRef.current === 'ecos' && row.type === 'CR' && rows.length === 0) {
            const unread = result.unread || [];
            toastRef.current(
              unread.length
                ? `No items to show for this ECO. The ${unread.join(' and ')} filter is not set in BOM settings, so its items can't be shown.`
                : 'This ECO lists no items that the Part Catalog or DMS items filters include.',
              'info'
            );
          }
        }

        // Step 2: for each child, probe ONE level deeper so we can render
        // leaves without a chevron. The probe is a full fetchChildren —
        // its rows are cached so the user's next click on this child is
        // instant. Probes whose target is already cached (sibling already
        // probed it) just read the cache; no network call.
        const enrichedRows = await mapWithLimit(rows, PROBE_CONCURRENCY, async (r) => {
          if (!r.originalId) return { ...r, hasChildren: false };
          if (probeRowsCache.current.has(r.originalId)) {
            const cached = probeRowsCache.current.get(r.originalId);
            return { ...r, hasChildren: cached.length > 0 };
          }
          try {
            const probe = await fetchChildRows(viewRef.current, r);
            probeRowsCache.current.set(r.originalId, probe.rows);
            return { ...r, hasChildren: probe.rows.length > 0 };
          } catch {
            return { ...r, hasChildren: undefined };
          }
        });

        childCache.current.set(nodeKey, { rows: enrichedRows, subtree: null });
        dispatch({ type: 'CHILD_LOAD_OK', parentKey: nodeKey, rows: enrichedRows });
        return enrichedRows;
      } catch (err) {
        if (err.httpCode === 401) {
          authExpiredRef.current?.(err.message);
          return null;
        }
        dispatch({ type: 'CHILD_LOAD_FAIL', parentKey: nodeKey });
        toastRef.current(err.message || 'Failed to load children.', 'error');
        return null;
      }
    },
    []
  );

  // ─── Toggle a row's expand/collapse ────────────────────────────────────
  // Stable identity: depends only on stable refs/callbacks.
  const toggle = useCallback(
    async (node) => {
      // The `node` argument may be stale (from an old render). Resolve the
      // current node from state via nodeKey.
      const current =
        nodesRef.current.find((n) => n.nodeKey === node.nodeKey) || node;

      if (current.expanded) {
        // Capture subtree for fast re-expand.
        const startIdx = findIndexByKey(nodesRef.current, current.nodeKey);
        let endIdx = startIdx + 1;
        while (
          endIdx < nodesRef.current.length &&
          nodesRef.current[endIdx].depth > current.depth
        ) {
          endIdx++;
        }
        const subtree = nodesRef.current.slice(startIdx + 1, endIdx);
        const cached = childCache.current.get(current.nodeKey) || {};
        childCache.current.set(current.nodeKey, { ...cached, subtree });
        dispatch({ type: 'COLLAPSE', parentKey: current.nodeKey });
        return;
      }

      // Expand from cached subtree if we have one.
      const cached = childCache.current.get(current.nodeKey);
      if (cached?.subtree) {
        dispatch({
          type: 'EXPAND_FROM_CACHE',
          parentKey: current.nodeKey,
          cachedSubtree: cached.subtree,
        });
        return;
      }

      // Otherwise fetch fresh.
      await loadChildren(current.nodeKey, current.row);
    },
    [loadChildren]
  );

  // ─── Direct children of a node, without touching tree state ────────────
  // Used by "Expand by category" to build the category list before anything
  // is expanded. Reads the same two caches as loadChildren, so the rows are
  // usually already in memory and the follow-up expand costs nothing extra.
  const getChildRows = useCallback(
    async (node) => {
      const current =
        nodesRef.current.find((n) => n.nodeKey === node.nodeKey) || node;
      const cached = childCache.current.get(current.nodeKey);
      if (cached?.rows) return cached.rows;
      const originalId = current.row.originalId;
      if (!originalId) return [];
      if (probeRowsCache.current.has(originalId)) {
        return probeRowsCache.current.get(originalId);
      }
      try {
        const res = await fetchChildRows(viewRef.current, current.row);
        probeRowsCache.current.set(originalId, res.rows);
        return res.rows;
      } catch (err) {
        if (err.httpCode === 401) {
          authExpiredRef.current?.(err.message);
          return null;
        }
        toastRef.current(err.message || 'Failed to load children.', 'error');
        return null;
      }
    },
    []
  );

  // ─── Exact sub-tree cost, on demand ────────────────────────────────────
  // Walks a row's ENTIRE sub-tree and returns the cost of the contents of ONE
  // unit of that part — the number the tree can't know until the whole
  // sub-tree has been seen. The caller gets the row's Total Cost with
  //
  //     total = rowQuantity × (own unit cost + this figure)
  //
  // Per-unit, not per-occurrence, because quantities multiply down a BOM — and
  // because that is what makes the result reusable for every other occurrence
  // of the same part.
  //
  // This is the only cost path that fetches. It runs when the user asks for it
  // (the ∑ button in the Total Cost cell), never on load, expand or paging —
  // which is what keeps the always-on rollup free.
  //
  // Memoized by `originalId`, so:
  //   - the same sub-assembly used in five places is walked once;
  //   - resolving a whole BOM also resolves every sub-assembly inside it;
  //   - rows already expanded on screen cost nothing (they're in the caches).
  //
  // Returns `{ total, resolved }`, or null on failure / 401 (the caller shows
  // nothing rather than a number it can't stand behind).
  //
  // `resolved` is EVERY part the walk settled — Map<originalId, per-unit
  // contents cost> — not just the row that was clicked. The walk has to compute all
  // of them anyway, so handing the whole set back means expanding a resolved
  // BOM shows its descendants' totals already filled in instead of a fresh
  // column of ∑ buttons. Each figure is per-unit, so it stays valid wherever
  // that part turns up.
  const resolveSubtreeCost = useCallback(
    async (node, onProgress) => {
      const current =
        nodesRef.current.find((n) => n.nodeKey === node.nodeKey) || node;
      const rootId = current.row.originalId;
      if (!rootId) return null;

      let scanned = 0;
      let failed = false;
      let authGone = false;

      // Every part this walk settles, for the caller to render. Includes parts
      // a previous walk had already cached — the UI may not have been told
      // about those yet.
      const resolved = new Map();

      // In-flight dedupe: two branches asking for the same part share one walk.
      const inFlight = new Map();

      // Rows for one parent, preferring anything already in memory.
      async function rowsFor(originalId) {
        const probed = probeRowsCache.current.get(originalId);
        if (probed) return probed;
        return limitFetch(async () => {
          // Re-check inside the limiter — a queued call may have been filled
          // by whoever ran ahead of it.
          const nowCached = probeRowsCache.current.get(originalId);
          if (nowCached) return nowCached;
          const res = await fetchChildren({
            parentOriginalId: originalId,
            pageSize: COST_WALK_PAGE_SIZE,
          });
          probeRowsCache.current.set(originalId, res.rows);
          scanned += res.rows.length;
          onProgress?.(scanned);
          return res.rows;
        });
      }

      // Contents cost of one unit of `originalId`:
      //   Σ over direct children c of  qty(c) × perUnit(c)
      // where perUnit is the child's own unit cost PLUS its own contents —
      // the same rule as computeCostRollup. Read the rule note there before
      // changing either.
      //
      // Depth-capped rather than cycle-tracked: a BOM that contains itself is
      // bad data, and the cap bounds it the same way expandAll is bounded.
      function descendantsSum(originalId, depth) {
        if (subtreeCostCache.current.has(originalId)) {
          const memo = subtreeCostCache.current.get(originalId);
          resolved.set(originalId, memo);
          return Promise.resolve(memo);
        }
        // No part id — nothing to walk into; it contributes no contents.
        if (!originalId) return Promise.resolve(0);
        // Hitting the depth cap means bad data (a BOM inside itself). Refuse
        // the whole walk rather than quietly reporting an understated total.
        if (depth >= EXPAND_ALL_DEPTH_CAP) {
          failed = true;
          return Promise.resolve(0);
        }
        const running = inFlight.get(originalId);
        if (running) return running;

        const p = (async () => {
          let rows;
          try {
            rows = await rowsFor(originalId);
          } catch (err) {
            if (err.httpCode === 401) { authGone = true; }
            failed = true;
            return 0;
          }

          const parts = await Promise.all(
            rows.map(async (r) => {
              // A row the probe already proved childless needs no call.
              const contents =
                r.hasChildren === false
                  ? 0
                  : await descendantsSum(r.originalId, depth + 1);
              // Own cost PLUS contents, with qty applying to both.
              return getQuantity(r) * (getUnitCost(r) + contents);
            })
          );
          const sum = parts.reduce((a, b) => a + b, 0);
          // Only keep a sum we know is whole. `failed` is walk-global, but the
          // ordering is safe: this line runs after every child has resolved, so
          // a failure anywhere beneath us has already set the flag.
          if (!failed) {
            subtreeCostCache.current.set(originalId, sum);
            resolved.set(originalId, sum);
          }
          return sum;
        })();

        inFlight.set(originalId, p);
        return p;
      }

      const total = await descendantsSum(rootId, 0);

      if (authGone) {
        authExpiredRef.current?.('Your session expired — please sign in again.');
        return null;
      }
      if (failed) {
        toastRef.current(
          `Couldn't reach every part under "${current.row.objName}" — no total shown.`,
          'error'
        );
        return null;
      }
      return { total, resolved };
    },
    []
  );

  // ─── Expand all descendants of a single root BOM ───────────────────────
  // BFS pump with concurrency cap. We pre-generate nodeKeys for each child
  // before dispatching, so the BFS frontier knows the keys without reading
  // post-dispatch state (which `useReducer` can't expose synchronously).
  //
  // `firstLevelFilter(row) => boolean` (optional) restricts which of the
  // START NODE's direct children are descended into — "Expand by category".
  // Every direct child is still attached and rendered; a child that fails the
  // predicate simply never joins the BFS frontier, so its own subtree is left
  // collapsed. Deeper levels are never filtered.
  const expandAll = useCallback(
    async (rootNode, onProgress, { firstLevelFilter = null } = {}) => {
      let done = 0;
      let stopped = false;

      const startCurrent =
        nodesRef.current.find((n) => n.nodeKey === rootNode.nodeKey) ||
        rootNode;

      // Depth of the node the user clicked. Only its direct children are
      // subject to `firstLevelFilter`; the frontier only ever grows downward,
      // so this depth uniquely identifies the start node inside the pump.
      const rootDepth = startCurrent.depth;

      // Frontier of nodes whose children we still need to load.
      let frontier = [
        {
          nodeKey: startCurrent.nodeKey,
          row: startCurrent.row,
          depth: startCurrent.depth,
        },
      ];

      const report = () =>
        onProgress?.(done, done + frontier.length);
      report();

      while (frontier.length > 0 && !stopped) {
        // Process up to CONCURRENCY items in parallel.
        const batchSize = Math.min(EXPAND_ALL_CONCURRENCY, frontier.length);
        const batch = frontier.splice(0, batchSize);
        const nextFrontier = [];

        await Promise.all(
          batch.map(async (item) => {
            if (stopped) return;

            // Depth cap: stop descending here.
            if (item.depth >= EXPAND_ALL_DEPTH_CAP) {
              done++;
              return;
            }

            const filterHere = firstLevelFilter && item.depth === rootDepth;

            // Already expanded in the DOM (the user clicked `>` first, which is
            // the normal way into "Expand by category"). Its children already
            // have real nodeKeys — descend using those. Dispatching here would
            // be a reducer no-op and would leave the frontier holding keys that
            // don't exist, silently stalling the whole walk.
            const live = nodesRef.current.find((n) => n.nodeKey === item.nodeKey);
            if (live?.expanded && live?.loaded) {
              const idx = nodesRef.current.indexOf(live);
              for (let i = idx + 1; i < nodesRef.current.length; i++) {
                const n = nodesRef.current[i];
                if (n.depth <= live.depth) break;
                if (n.depth !== live.depth + 1) continue;
                if (filterHere && !firstLevelFilter(n.row)) continue;
                nextFrontier.push({ nodeKey: n.nodeKey, row: n.row, depth: n.depth });
              }
              done++;
              return;
            }

            let rows;
            try {
              const cached = childCache.current.get(item.nodeKey);
              if (cached?.rows) {
                rows = cached.rows;
              } else {
                const res = await fetchChildRows(viewRef.current, item.row);
                rows = res.rows;
                childCache.current.set(item.nodeKey, {
                  rows,
                  subtree: null,
                });
              }
            } catch (err) {
              if (err.httpCode === 401) {
                stopped = true;
                authExpiredRef.current?.(err.message);
                return;
              }
              toastRef.current(
                err.message ||
                  `Failed to load children of "${item.row.objName}".`,
                'error'
              );
              done++;
              return;
            }

            // Pre-generate keys for the new children, dispatch with them,
            // and queue them for the next BFS round using those same keys.
            const childKeys = rows.map(() => nextNodeKey());
            dispatch({
              type: 'CHILD_LOAD_OK',
              parentKey: item.nodeKey,
              rows,
              childKeys,
            });

            for (let i = 0; i < rows.length; i++) {
              // Attached and rendered either way — only the descent is skipped.
              if (filterHere && !firstLevelFilter(rows[i])) continue;
              nextFrontier.push({
                nodeKey: childKeys[i],
                row: rows[i],
                depth: item.depth + 1,
              });
            }
            done++;
          })
        );

        frontier = frontier.concat(nextFrontier);
        report();
      }

      // Final progress.
      onProgress?.(done, done);
    },
    []
  );

  // ─── Where-Used "Locate" — true level-order BFS ────────────────────────
  // At each round we fully expand the current frontier (every assembly's
  // children fetched, in parallel up to EXPAND_ALL_CONCURRENCY). Once the
  // whole level is expanded we scan the newly-added rows for the target;
  // first match wins. If nothing matches, the next round's frontier is the
  // assemblies among those new rows. Stops as soon as the target is found.
  //
  // Match predicate: descendant.cs21Raw === target  OR  descendant.cs21Int
  // === target. The same `target` value is what was sent into
  // dbo.fn_GetRootParentByCS21() when entering Where Used:
  //   - PRT click → target = the part's own id (matches PI cs21Int).
  //   - PI  click → target = the source PI's raw CS21 (matches cs21Raw).
  const findAndExpand = useCallback(
    async (rootNode, { target, itemId }) => {
      const t = String(target ?? '').trim();
      // A SHARED row names the exact occurrence by its own item id, which Where-Used's
      // CS21 match cannot express: two instances of the same source part carry the same
      // CS21, so matching on it alone lands on whichever occurrence is found first.
      // Either value alone is enough to search on.
      const wantId = String(itemId ?? '').trim();
      if (!t && !wantId) return null;

      let foundKey = null;
      let stopped = false;

      const startCurrent =
        nodesRef.current.find((n) => n.nodeKey === rootNode.nodeKey) ||
        rootNode;

      let frontier = [
        {
          nodeKey: startCurrent.nodeKey,
          row: startCurrent.row,
          depth: startCurrent.depth,
        },
      ];

      while (frontier.length > 0 && !stopped) {
        // Fetch children for every assembly in the frontier, in parallel.
        // If a node is already expanded in the tree we skip the fetch and
        // read its children directly from nodesRef — their nodeKeys are the
        // real ones already rendered in the DOM, so the highlight works even
        // when the user had expanded the tree before clicking Locate.
        const newChildren = [];

        await mapWithLimit(
          frontier,
          EXPAND_ALL_CONCURRENCY,
          async (item) => {
            if (stopped) return;
            if (item.depth >= EXPAND_ALL_DEPTH_CAP) return;

            const live = nodesRef.current.find((n) => n.nodeKey === item.nodeKey);
            if (live?.expanded && live?.loaded) {
              // Already in the DOM — collect direct children from nodesRef.
              const idx = nodesRef.current.findIndex((n) => n.nodeKey === item.nodeKey);
              for (let i = idx + 1; i < nodesRef.current.length; i++) {
                const n = nodesRef.current[i];
                if (n.depth <= live.depth) break;
                if (n.depth === live.depth + 1) {
                  newChildren.push({ nodeKey: n.nodeKey, row: n.row, depth: n.depth });
                }
              }
              return;
            }

            let rows;
            try {
              const cached = childCache.current.get(item.nodeKey);
              if (cached?.rows) {
                rows = cached.rows;
              } else {
                const res = await fetchChildRows(viewRef.current, item.row);
                rows = res.rows;
                childCache.current.set(item.nodeKey, {
                  rows,
                  subtree: null,
                });
              }
            } catch (err) {
              if (err.httpCode === 401) {
                stopped = true;
                authExpiredRef.current?.(err.message);
                return;
              }
              toastRef.current(
                err.message ||
                  `Failed to load children of "${item.row.objName}".`,
                'error'
              );
              return;
            }

            const childKeys = rows.map(() => nextNodeKey());
            dispatch({
              type: 'CHILD_LOAD_OK',
              parentKey: item.nodeKey,
              rows,
              childKeys,
            });

            for (let i = 0; i < rows.length; i++) {
              newChildren.push({
                nodeKey: childKeys[i],
                row: rows[i],
                depth: item.depth + 1,
              });
            }
          }
        );

        if (stopped) break;

        // Scan the level we just expanded — first match wins.
        for (const c of newChildren) {
          const cs21Raw = String(c.row.cs21Raw || '').trim();
          const cs21Int = String(c.row.cs21Int || '').trim();
          const rowId = String(c.row.itemId || '').trim();
          if ((t && (cs21Raw === t || (cs21Int && cs21Int === t)))
              || (wantId && rowId && rowId === wantId)) {
            foundKey = c.nodeKey;
            stopped = true;
            break;
          }
        }
        if (stopped) break;

        // Next round: only nodes that aren't known leaves. Unprobed rows
        // (hasChildren undefined) are kept since we can't yet rule them out.
        frontier = newChildren.filter((c) => c.row.hasChildren !== false);
      }

      return foundKey;
    },
    []
  );

  return {
    nodes: state.nodes,
    topLoading: state.topLoading,
    topError: state.topError,
    topPage: state.topPage,
    topPageSize: state.topPageSize,
    topTotal: state.topTotal,
    topHasMore: state.topHasMore,
    loadTop,
    toggle,
    expandAll,
    getChildRows,
    findAndExpand,
    resolveSubtreeCost,
  };
}
